import { ProductRecord, IncludeExclude, AUTOCOMPLETE_ITEM_PADDING } from "../../../utils/types";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import { InputGroup } from "react-bootstrap";
import AutocompleteIncludeExclude from "../../library/AutocompleteIncludeExclude";
import { isAllowedFilterField } from "../../../utils/UserHelper";
import "../../library/Typeahead.css";

// Extended type with computed label for allowNew compatibility
interface ProductRecordWithLabel extends ProductRecord {
  label: string;
}

interface ProductsAutocompleteProps {
  onChange: (selected: ProductRecord[]) => void;
  options: ProductRecord[] | null;
  selected: ProductRecord[];
  reactRef: any;
  disabled?: boolean;
  defaultIncludeExclude?: IncludeExclude;
  includeExcludeOnChange?: (newIncludeExclude: IncludeExclude) => void;
  onInputChange?: (query: string) => void;
  isLoading?: boolean;
}

const MIN_TYPEAHEAD_LENGTH = 3;

// Helper to compute label from ProductRecord
// Only show "id - name" format if productName exists and differs from productId
const computeLabel = (option: ProductRecord): string => {
  if (option.productName && option.productName !== option.productId) {
    return `${option.productId} - ${option.productName}`;
  }
  return option.productId || '';
};

function ProductsAutocomplete(props: ProductsAutocompleteProps) {
  // Bypass client-side filtering - results are already filtered by the search endpoint
  const filterBy = () => true;

  // Transform options to add computed label property (required for allowNew)
  const optionsWithLabel: ProductRecordWithLabel[] = (props.options || []).map(opt => ({
    ...opt,
    label: computeLabel(opt)
  }));

  // Transform selected items similarly
  const selectedWithLabel: ProductRecordWithLabel[] = (props.selected || []).map(opt => ({
    ...opt,
    label: computeLabel(opt)
  }));

  // Handle selection change - convert back to ProductRecord[]
  const handleChange = (selected: any[]) => {
    const records: ProductRecord[] = selected.map(item => {
      // Handle custom new entries (from allowNew)
      if (item.customOption) {
        return {
          productId: item.label,
          productName: null
        } as ProductRecord;
      }
      // Regular selection - remove the computed label
      const { label, ...record } = item;
      return record as ProductRecord;
    });
    props.onChange(records);
  };

  const renderMenuItemChildren = (option: ProductRecordWithLabel) => {
    return <div style={{padding: AUTOCOMPLETE_ITEM_PADDING}}><span>{option.label}</span></div>;
  };

  return (
    <InputGroup>
      {isAllowedFilterField("productIdsExclude") && (
        <AutocompleteIncludeExclude
          defaultIncludeExclude={props.defaultIncludeExclude}
          includeExcludeOnChange={props.includeExcludeOnChange}
          disabled={props.disabled}
        />
      )}
      <AsyncTypeahead
        id="productsAutocomplete"
        size="sm"
        filterBy={filterBy}
        isLoading={props.isLoading || false}
        labelKey="label"
        minLength={MIN_TYPEAHEAD_LENGTH}
        onSearch={props.onInputChange || (() => {})}
        options={optionsWithLabel}
        selected={selectedWithLabel}
        onChange={handleChange}
        placeholder={`Type ${MIN_TYPEAHEAD_LENGTH}+ chars to search products...`}
        renderMenuItemChildren={renderMenuItemChildren}
        multiple
        clearButton
        ref={props.reactRef}
        useCache={false}
        disabled={props.disabled}
        allowNew
        newSelectionPrefix="Custom filter: "
      />
    </InputGroup>
  );
}

export default ProductsAutocomplete;
